
import { supabase } from "@/integrations/supabase/client";
import { 
  FeatureImplementation, 
  ImplementationTask, 
  ImplementationStatusCounts 
} from "@/lib/types/feature-management";
import { toast } from "sonner";

// Fetch all feature implementations
export const fetchFeatureImplementations = async (): Promise<FeatureImplementation[]> => {
  const { data, error } = await supabase
    .from('feature_implementations')
    .select('*')
    .order('last_updated', { ascending: false });

  if (error) {
    console.error('Error fetching feature implementations:', error);
    throw error;
  }

  return data as FeatureImplementation[];
};

// Fetch a single implementation by ID
export const fetchImplementationById = async (id: string): Promise<FeatureImplementation | null> => {
  const { data, error } = await supabase
    .from('feature_implementations')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Error fetching implementation with ID ${id}:`, error);
    return null;
  }

  return data as FeatureImplementation;
};

// Create a new implementation
export const createImplementation = async (implementation: Omit<FeatureImplementation, 'id' | 'created_at' | 'updated_at'>): Promise<FeatureImplementation> => {
  const { data, error } = await supabase
    .from('feature_implementations')
    .insert(implementation)
    .select()
    .single();

  if (error) {
    console.error('Error creating implementation:', error);
    toast.error('Failed to create implementation');
    throw error;
  }

  toast.success('Implementation created successfully');
  return data as FeatureImplementation;
};

// Update an existing implementation
export const updateImplementation = async (id: string, updates: Partial<FeatureImplementation>): Promise<FeatureImplementation> => {
  const { data, error } = await supabase
    .from('feature_implementations')
    .update({ ...updates, last_updated: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating implementation with ID ${id}:`, error);
    toast.error('Failed to update implementation');
    throw error;
  }

  toast.success('Implementation updated successfully');
  return data as FeatureImplementation;
};

// Delete an implementation
export const deleteImplementation = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('feature_implementations')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting implementation with ID ${id}:`, error);
    toast.error('Failed to delete implementation');
    throw error;
  }

  toast.success('Implementation deleted successfully');
};

// Fetch tasks for an implementation
export const fetchTasksByImplementationId = async (implementationId: string): Promise<ImplementationTask[]> => {
  const { data, error } = await supabase
    .from('implementation_tasks')
    .select('*')
    .eq('implementation_id', implementationId)
    .order('due_date', { ascending: true });

  if (error) {
    console.error(`Error fetching tasks for implementation ${implementationId}:`, error);
    return [];
  }

  return data as ImplementationTask[];
};

// Create a new task
export const createTask = async (task: Omit<ImplementationTask, 'id' | 'created_at' | 'updated_at'>): Promise<ImplementationTask> => {
  const { data, error } = await supabase
    .from('implementation_tasks')
    .insert(task)
    .select()
    .single();

  if (error) {
    console.error('Error creating task:', error);
    toast.error('Failed to create task');
    throw error;
  }

  toast.success('Task created successfully');
  return data as ImplementationTask;
};

// Update an existing task
export const updateTask = async (id: string, updates: Partial<ImplementationTask>): Promise<ImplementationTask> => {
  const { data, error } = await supabase
    .from('implementation_tasks')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating task with ID ${id}:`, error);
    toast.error('Failed to update task');
    throw error;
  }

  toast.success('Task updated successfully');
  return data as ImplementationTask;
};

// Delete a task
export const deleteTask = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('implementation_tasks')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting task with ID ${id}:`, error);
    toast.error('Failed to delete task');
    throw error;
  }

  toast.success('Task deleted successfully');
};

// Get status counts for dashboard stats
export const getImplementationStatusCounts = async (): Promise<ImplementationStatusCounts> => {
  const { data, error } = await supabase
    .from('feature_implementations')
    .select('status');

  if (error) {
    console.error('Error fetching implementation status counts:', error);
    return {
      planning: 0,
      inProgress: 0,
      testing: 0,
      completed: 0
    };
  }

  const counts: ImplementationStatusCounts = {
    planning: data.filter(item => item.status === 'planning').length,
    inProgress: data.filter(item => item.status === 'in-progress').length,
    testing: data.filter(item => item.status === 'testing').length,
    completed: data.filter(item => item.status === 'completed').length
  };

  return counts;
};
